// Shared CSV + Excel export helpers for simple tabular data.
// Used by the System Maintenance pages (Users, Country, Clause, ChargesCode, PortPlace)
// to keep the "Export" / "Export to Excel" buttons consistent across the module.

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const HEADER_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E40AF" },
};

const ALT_ROW_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
};

/**
 * Column descriptor used by the export helpers.
 * @typedef {Object} ExportColumn
 * @property {string} key                The property name of the row.
 * @property {string} label              The human-readable column header.
 * @property {(row:any) => any} [accessor] Optional function to derive the cell value.
 * @property {number} [width]            Optional Excel column width.
 */

const safeFileName = (name = "Export") =>
    String(name).replace(/[^\w\-]+/g, "_") || "Export";

const formatCellValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return value;
};

const getCellValue = (row, col) => {
    if (typeof col.accessor === "function") return col.accessor(row);
    return row?.[col.key];
};

const csvEscape = (value) => {
    const str = formatCellValue(value);
    const text = String(str ?? "");
    if (/[",\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

/**
 * Download tabular data as a CSV file.
 * @param {{ fileName: string, columns: ExportColumn[], rows: any[] }} options
 */
export function exportRowsToCsv({ fileName = "Export", columns = [], rows = [] }) {
    if (!Array.isArray(columns) || columns.length === 0) {
        throw new Error("exportRowsToCsv: `columns` must be a non-empty array");
    }
    const safeRows = Array.isArray(rows) ? rows : [];

    const header = columns.map((c) => csvEscape(c.label)).join(",");
    const body = safeRows
        .map((row) =>
            columns.map((col) => csvEscape(getCellValue(row, col))).join(",")
        )
        .join("\r\n");

    const csv = `${header}\r\n${body}`;
    const blob = new Blob([`\ufeff${csv}`], {
        type: "text/csv;charset=utf-8;",
    });
    saveAs(
        blob,
        `${safeFileName(fileName)}_${new Date().toISOString().split("T")[0]}.csv`
    );
}

/**
 * Download tabular data as an XLSX file with a styled header row.
 * @param {{ fileName: string, sheetName?: string, columns: ExportColumn[], rows: any[] }} options
 */
export async function exportRowsToExcel({
    fileName = "Export",
    sheetName = "Sheet1",
    columns = [],
    rows = [],
}) {
    if (!Array.isArray(columns) || columns.length === 0) {
        throw new Error("exportRowsToExcel: `columns` must be a non-empty array");
    }
    const safeRows = Array.isArray(rows) ? rows : [];

    const workbook = new ExcelJS.Workbook();
    const safeSheetName =
        String(sheetName || "Sheet1")
            .replace(/[\\/*?:\[\]]/g, " ")
            .slice(0, 31) || "Sheet1";
    const ws = workbook.addWorksheet(safeSheetName);

    ws.columns = columns.map((col) => ({
        header: String(col.label || "").toUpperCase(),
        key: col.key,
        width: col.width || 18,
    }));

    const headerRow = ws.getRow(1);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = HEADER_FILL;
        cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    headerRow.height = 22;

    safeRows.forEach((row, index) => {
        const line = {};
        columns.forEach((col) => {
            line[col.key] = formatCellValue(getCellValue(row, col));
        });
        const dataRow = ws.addRow(line);
        if (index % 2 === 1) {
            dataRow.eachCell((cell) => {
                cell.fill = ALT_ROW_FILL;
            });
        }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${safeFileName(fileName)}_${new Date().toISOString().split("T")[0]}.xlsx`
    );
}
